import * as React from 'react';
import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/nextjs';
import { FirebaseError } from 'firebase/app';
import {
  AuthCredential,
  FacebookAuthProvider,
  GoogleAuthProvider,
  User,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  FirebasePopupProvider,
  SOCIAL_AUTH_PROVIDER_LABEL,
  SocialAuthProvider,
} from 'constants/auth';
import { GetExistingUserQuery } from 'types/generated/client';
import { auth, facebookProvider } from 'services/client/firebase';
import { pollForNewUser } from 'services/client/pollForNewUser';
import { syncLinkedProvider } from 'services/client/syncLinkedProvider';
import { startVairSignIn } from 'services/client/vairSignIn';
import { detectFraudFromBrowser } from 'utils/shared/fraud/detectFraud';
import { StatePayloadValue, getFirebaseErrorCode, useOAuthSignIn } from './useOAuthSignIn';

// The row pollForNewUser resolves to (GET_AUTH_USER). It returns Promise<unknown>, so we cast.
type PolledUser = GetExistingUserQuery['users'][number] | null | undefined;

interface AuthenticateParams {
  /** Values captured on the form before the popup (username, account type, lat/lng). */
  statePayload?: Record<string, StatePayloadValue>;
  /**
   * Called once an EXISTING user is authenticated and their DB row is confirmed — the form
   * uses this to redirect. Receives the polled DB user so callers can, e.g., fire
   * `handleSignupSuccess`. Brand-new users are NOT redirected: they land as `pending` and the
   * global ModalCompleteProfile takes over in place.
   */
  onExistingUser: (dbUser: PolledUser) => void | Promise<void>;
  /**
   * Same-origin path to land on after a redirect-based provider (VAIR) signs an existing user
   * in. Popup providers stay on the page, so they use `onExistingUser` instead.
   */
  returnTo?: string;
}

/**
 * Active account-linking prompt. Set when an OAuth sign-in collides with an existing account
 * that uses a different provider (`auth/account-exists-with-different-credential`). Drives
 * ModalAccountLinking, which asks the user to verify with a method they already have before we
 * attach the new provider's credential.
 */
export interface OAuthLinkingState {
  email: string;
  /** The provider the user just tried (and which we'll link after they verify). */
  newProvider: FirebasePopupProvider;
  /** Sign-in methods already on the account (may be empty under email-enumeration protection). */
  existingMethods: string[];
}

const credentialFromError = (
  provider: FirebasePopupProvider,
  error: FirebaseError,
): AuthCredential | null => {
  // credentialFromError is a static on the attempted provider's class.
  return provider === 'google'
    ? GoogleAuthProvider.credentialFromError(error)
    : FacebookAuthProvider.credentialFromError(error);
};

// A fresh provider for the verification popup. For Google we pin `login_hint` to the collision
// email so the user lands on the right account (avoids linking to the wrong Google account).
const buildVerificationProvider = (provider: FirebasePopupProvider, email: string) => {
  if (provider === 'google') {
    const google = new GoogleAuthProvider();
    google.addScope('email');
    google.setCustomParameters({ login_hint: email });
    return google;
  }
  return facebookProvider;
};

const mapVerificationError = (error: unknown, email: string): string => {
  switch (getFirebaseErrorCode(error)) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Verification was cancelled.';
    case 'auth/popup-blocked':
      return 'Popup was blocked by your browser.';
    case 'auth/credential-already-in-use':
      return 'That login is already connected to another account.';
    case 'auth/user-mismatch':
      return `That doesn't match ${email}. Please use the account for ${email}.`;
    default:
      return 'We could not verify your account. Please try again.';
  }
};

const mapPasswordError = (error: unknown): string => {
  switch (getFirebaseErrorCode(error)) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'Incorrect password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again in a few minutes.';
    default:
      return 'We could not sign you in. Please try again.';
  }
};

/**
 * Shared Google/Facebook authentication flow for the login and signup forms. Owns the popup,
 * fraud check, the token/claims handshake (via pollForNewUser — the same path email signup
 * uses), routing new-vs-existing users, and account-linking when a sign-in collides with an
 * existing account on the same email.
 */
export const useOAuthAuthentication = () => {
  const client = useApolloClient();
  const { signInWithProvider, isLoading } = useOAuthSignIn();
  const [error, setError] = React.useState<Error | null>(null);

  // Account-linking state (see OAuthLinkingState). The pending credential + continuation are
  // held in refs so re-renders while the linking modal is open don't drop them.
  // True while the browser is on its way to VAIR's consent screen (no popup to await).
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [linking, setLinking] = React.useState<OAuthLinkingState | null>(null);
  const [linkingError, setLinkingError] = React.useState<Error | null>(null);
  const [isLinking, setIsLinking] = React.useState(false);
  const pendingCredentialRef = React.useRef<AuthCredential | null>(null);
  const onExistingUserRef = React.useRef<AuthenticateParams['onExistingUser'] | null>(null);

  const finalizeAuthenticatedUser = async (firebaseUser: User) => {
    // Forces the token refresh + waits for the DB row (same path email signup uses).
    return (await pollForNewUser(client, firebaseUser)) as PolledUser;
  };

  const authenticateWithProvider = async (
    provider: SocialAuthProvider,
    { statePayload, onExistingUser, returnTo }: AuthenticateParams,
  ) => {
    setError(null);

    const fraudDetection = detectFraudFromBrowser();
    if (fraudDetection.isFraud) {
      Sentry.captureException(new Error('Fraud attempt detected during OAuth sign in'), {
        extra: {
          provider,
          reason: fraudDetection.reason,
          timezone: fraudDetection.timezone,
          locale: fraudDetection.locale,
        },
      });
      setError(new Error('Unable to continue. Please try again later.'));
      return;
    }

    // VAIR is a full-page redirect; the /auth/vair page finishes the sign-in and routes.
    // Stays "loading" through the navigation so the buttons can't be fired twice — a second
    // click would reissue the flow cookie and strand the first tab's callback.
    if (provider === 'vair') {
      setIsRedirecting(true);
      try {
        await startVairSignIn({ statePayload, returnTo });
      } catch (redirectError) {
        Sentry.captureException(redirectError, {
          extra: { context: 'useOAuthAuthentication:startVairSignIn' },
        });
        setIsRedirecting(false);
        setError(
          new Error(
            `We could not start ${SOCIAL_AUTH_PROVIDER_LABEL.vair} sign in. Please try again.`,
          ),
        );
      }
      return;
    }

    const result = await signInWithProvider(provider, { statePayload });

    // Same email, different provider → open the account-linking flow instead of erroring.
    if (result.code === 'auth/account-exists-with-different-credential') {
      const firebaseError = result.firebaseError;
      const email =
        firebaseError instanceof FirebaseError
          ? (firebaseError.customData?.email as string | undefined)
          : undefined;
      const pendingCredential =
        firebaseError instanceof FirebaseError
          ? credentialFromError(provider, firebaseError)
          : null;

      if (!email || !pendingCredential) {
        Sentry.captureException(new Error('OAuth collision missing email or pending credential'), {
          extra: { provider },
        });
        setError(
          new Error(
            'An account already exists with this email. Please sign in with your original method.',
          ),
        );
        return;
      }

      let existingMethods: string[] = [];
      try {
        existingMethods = await fetchSignInMethodsForEmail(auth, email);
      } catch (methodsError) {
        // Email-enumeration protection returns []/throws; the modal falls back to all options.
        existingMethods = [];
      }

      pendingCredentialRef.current = pendingCredential;
      onExistingUserRef.current = onExistingUser;
      setLinkingError(null);
      setLinking({ email, newProvider: provider, existingMethods });
      return;
    }

    if (result.error) {
      setError(result.error);
      return;
    }

    if (!result.userCredential) {
      setError(new Error('Sign in failed. Please try again.'));
      return;
    }

    // Facebook can return an account with no email. Our users.email column is NOT NULL, so the
    // processSignUp trigger can't create a DB row and deletes this dud Firebase user server-side.
    // Bail here with clear feedback instead of polling 60s for a row that will never exist.
    const firebaseUser = result.userCredential.user;
    if (!firebaseUser.email) {
      Sentry.captureException(new Error('OAuth sign-in returned no email'), {
        extra: { context: 'useOAuthAuthentication:noEmail', provider },
      });
      await signOut(auth).catch(() => undefined);
      const providerLabel = SOCIAL_AUTH_PROVIDER_LABEL[provider];
      setError(
        new Error(
          `Your ${providerLabel} account didn't share an email with us. Please use a different sign-in method.`,
        ),
      );
      return;
    }

    let dbUser: PolledUser;
    try {
      dbUser = await finalizeAuthenticatedUser(firebaseUser);
    } catch (pollError) {
      Sentry.captureException(pollError, {
        extra: { context: 'useOAuthAuthentication:pollForNewUser', provider },
      });
      setError(new Error('Account setup is taking longer than expected. Please refresh the page.'));
      return;
    }

    // New users are flagged `isPendingAccountDetails` → ModalCompleteProfile appears here.
    if (result.isNewUser) {
      return;
    }

    await onExistingUser(dbUser);
  };

  const resetLinking = () => {
    pendingCredentialRef.current = null;
    onExistingUserRef.current = null;
    setLinking(null);
    setLinkingError(null);
    setIsLinking(false);
  };

  // Shared tail: attach the pending credential to the just-verified account, then continue as a
  // normal existing-user sign-in (poll + redirect). The linking modal closes on success.
  const attachAndContinue = async (verifiedUser: User) => {
    const pendingCredential = pendingCredentialRef.current;
    const onExistingUser = onExistingUserRef.current;
    if (!pendingCredential || !onExistingUser || !verifiedUser) {
      throw new Error('Linking session expired. Please try again.');
    }

    await linkWithCredential(verifiedUser, pendingCredential);

    // Best-effort: record the linked provider in the DB. The `user` role can't write
    // latestAuthProvider / auth-identities, so an admin route reconciles them from Firebase.
    // Fire-and-forget — a sync hiccup must never block the sign-in.
    if (linking) {
      syncLinkedProvider(linking.newProvider).catch((syncError) =>
        Sentry.captureException(syncError, {
          extra: { context: 'useOAuthAuthentication:syncLinkedProvider' },
        }),
      );
    }

    const dbUser = await finalizeAuthenticatedUser(verifiedUser);
    resetLinking();
    await onExistingUser(dbUser);
  };

  // Verify by signing into an existing federated provider (Google/Facebook), then link.
  const linkWithExistingProvider = async (existingProvider: FirebasePopupProvider) => {
    if (!linking) return;
    setIsLinking(true);
    setLinkingError(null);
    try {
      const verificationProvider = buildVerificationProvider(existingProvider, linking.email);
      const credential = await signInWithPopup(auth, verificationProvider);
      await attachAndContinue(credential.user);
    } catch (linkError) {
      Sentry.captureException(linkError, {
        extra: { context: 'useOAuthAuthentication:linkWithExistingProvider', existingProvider },
      });
      setLinkingError(new Error(mapVerificationError(linkError, linking.email)));
      setIsLinking(false);
    }
  };

  // Verify with the account's password, then link.
  const linkWithPassword = async (password: string) => {
    if (!linking) return;
    setIsLinking(true);
    setLinkingError(null);
    try {
      const credential = await signInWithEmailAndPassword(auth, linking.email, password);
      await attachAndContinue(credential.user);
    } catch (linkError) {
      Sentry.captureException(linkError, {
        extra: { context: 'useOAuthAuthentication:linkWithPassword' },
      });
      setLinkingError(new Error(mapPasswordError(linkError)));
      setIsLinking(false);
    }
  };

  return {
    authenticateWithProvider,
    isLoading: isLoading || isRedirecting,
    error,
    setError,
    // Account linking
    linking,
    linkingError,
    isLinking,
    linkWithExistingProvider,
    linkWithPassword,
    cancelLinking: resetLinking,
  };
};
